import _ from 'lodash';

import type { FilterOption, FilterSidePanelProps, PotencyRange } from '@dutchiesdk/ecommerce-extensions-sdk';
import type { FilterVisibility, MenuFilterOptions, MenuOption } from 'components/menu-sidebar/types';
import { CategoryPotencyRanges, CategoryPotencyUnits } from 'shared/constants';
import type { SpecialMenuCard } from 'src/gql/graphql';
import { getEffectsAsOptions } from 'utils/helpers/effects-filtering';
import { gqlFilterWeightToQueryParamValue } from 'utils/helpers/product/weight-filter-converters';
import { FILTERABLE_KEYS, getFilterLabel, specialsMatchFilterKey } from 'utils/helpers/special-filter';

export type FilterSidePanelQueryParams = {
  brands: string[];
  category?: string;
  effects: string[];
  potencycbd: PotencyRange;
  potencythc: PotencyRange;
  specialFilter?: string[];
  straintypes: string[];
  subcategories: string[];
  terpenes: string[];
  weight?: string;
};

type Callbacks = Pick<
  FilterSidePanelProps,
  'onClearAll' | 'onSetCategory' | 'onSetPotencyRange' | 'onSetSubcategories' | 'onSetWeight' | 'onToggleFilter'
>;

type Input = {
  callbacks: Callbacks;
  categoryKey?: string;
  context: FilterSidePanelProps['context'];
  hideSalesLanguage: boolean;
  isLoading: boolean;
  menuFilterOptions: MenuFilterOptions;
  queryParams: FilterSidePanelQueryParams;
  specials?: SpecialMenuCard[];
  visibility: FilterVisibility;
};

const mapOption = ({ key, label, value }: MenuOption): FilterOption => ({ key, label, value });

const mapSpecialOptions = (hideSalesLanguage: boolean, specials: SpecialMenuCard[]): FilterOption[] =>
  FILTERABLE_KEYS.filter((key) => specialsMatchFilterKey(key, specials))
    .map((key) => ({
      key,
      label: getFilterLabel(key, hideSalesLanguage),
      value: key,
    }));

const mapPotency = (
  heading: string,
  categoryKey: string | undefined,
  selected: PotencyRange,
  available: boolean
): FilterSidePanelProps['filters']['potencyThc'] => {
  const [min, max] = _.get(CategoryPotencyRanges, categoryKey ?? '', [0, 50]) as PotencyRange;

  return {
    available,
    heading,
    min,
    max,
    step: 1,
    unit: _.get(CategoryPotencyUnits, categoryKey ?? '', '%'),
    selected,
  };
};

/** Maps marketplace filter state into the stable SDK FilterSidePanelProps contract. */
export const mapToFilterSidePanelProps = ({
  callbacks,
  categoryKey,
  context,
  hideSalesLanguage,
  isLoading,
  menuFilterOptions,
  queryParams,
  specials = [],
  visibility,
}: Input): FilterSidePanelProps => ({
  isLoading,
  context,
  filters: {
    categories: {
      available: visibility.showCategorySubcategoryFilter,
      heading: 'Categories',
      categories: (menuFilterOptions.categoriesSubcategories ?? []).map(({ category, subcategories }) => ({
        ...mapOption(category),
        subcategories: subcategories.map(mapOption),
      })),
      selectedCategory: queryParams.category ?? null,
      selectedSubcategories: queryParams.subcategories,
      allowsMultipleSubcategories: visibility.isMultiSelectSubcategoryFilterEnabled,
    },
    subcategories: {
      available: visibility.showSubcategoryFilter,
      heading: 'Subcategories',
      options: (menuFilterOptions.subcategories ?? []).map(mapOption),
      selected: queryParams.subcategories,
    },
    specials: {
      available: visibility.showSpecialsFilter,
      heading: hideSalesLanguage ? 'Offers' : 'Sales & Offers',
      options: mapSpecialOptions(hideSalesLanguage, specials),
      selected: queryParams.specialFilter ?? [],
    },
    weights: {
      available: visibility.showWeights,
      heading: 'Weights',
      options: (menuFilterOptions.weights ?? []).map((weight) => ({
        key: weight,
        label: weight,
        value: gqlFilterWeightToQueryParamValue(weight),
      })),
      selected: queryParams.weight ?? null,
    },
    brands: {
      available: visibility.showBrands,
      heading: 'Brands',
      options: _.uniqBy(menuFilterOptions.brands ?? [], 'name').map((brand) => ({
        key: brand.id,
        label: brand.name,
        value: _.kebabCase(brand.name),
        imageUrl: brand.imageUrl ?? null,
        description: brand.description ?? null,
      })),
      selected: queryParams.brands,
    },
    strainTypes: {
      available: visibility.showStrainTypes,
      heading: 'Types',
      options: (menuFilterOptions.strainTypes ?? []).map(mapOption),
      selected: queryParams.straintypes,
    },
    effects: {
      available: visibility.showEffects,
      heading: 'Effects',
      options: getEffectsAsOptions(menuFilterOptions.effects ?? []).map(mapOption),
      selected: queryParams.effects,
    },
    terpenes: {
      available: visibility.showTerpenes,
      heading: 'Terpenes',
      options: _.uniqBy(menuFilterOptions.terpenes ?? [], 'name').map((terpene) => ({
        key: terpene.compound_id,
        label: terpene.name,
        value: _.kebabCase(terpene.name),
        compoundId: terpene.compound_id,
        potencyId: terpene.potency_id,
        values: terpene.values,
        unit: terpene.unit,
        unitSymbol: terpene.unitSymbol,
      })),
      selected: queryParams.terpenes,
    },
    potencyThc: mapPotency('THC', categoryKey, queryParams.potencythc, visibility.showPotencies),
    potencyCbd: mapPotency('CBD', categoryKey, queryParams.potencycbd, visibility.showPotencies),
  },
  ...callbacks,
});
